/**
 * inscreverEsteAparelho — a ÚNICA rotina que inscreve este aparelho no push (plano "Push: religar sozinho ou avisar", 29/10/2026).
 *
 * O clique em "Ligar" (`usePushSubscription.subscribe`), o religar sozinho e a renovação (`sincronizarPush`) faziam, ou iam fazer, três
 * cópias do mesmo caminho: registrar o service worker, buscar a chave VAPID, `pushManager.subscribe()`, `POST /subscribe`. Uma cópia só
 * não diverge: o que muda entre os três casos é a `origem` (vai no corpo do POST, para o log do servidor) e o que se grava no aparelho.
 *
 * Esta rotina NÃO pede permissão: quem pede é o clique (`Notification.requestPermission()` exige gesto). O religar sozinho só roda com a
 * permissão já `granted`, e o `subscribe()` do PushManager não exige gesto nesse caso.
 *
 * Gravações (só depois de o POST dar certo — Decisões 2 e 3):
 * - dono do push (`donoDoPush.ts`): `clique` toma o aparelho para este app; as outras origens só adotam quando ninguém é dono (a
 *   reinscrição automática respeita o dono, como sempre: decisão do Augusto de 12/10/2026, "só num clique");
 * - intenção `ligado` (`memoriaDoPush.ts`);
 * - confirmação do app: a marca do endpoint que o servidor aceitou (é ela que deixa o `sincronizarPush` distinguir "o servidor apagou a
 *   linha" de "o servidor nunca a teve").
 * Um "Sair" no meio (`aindaVale() === false`) não grava nada disso: ficaria uma marca órfã, sem sessão por trás.
 *
 * O Desligar não é desfeito por um religar (Review Focus 1): fora do clique, a intenção é relida DEPOIS da última espera de rede
 * (`vapid-key`) e ANTES de mexer no navegador. Um Desligar gravado durante a rede, noutra aba ou nesta, para o religar com
 * `DesligadoDePropositoError`, sem `unsubscribe`, sem `subscribe` e sem POST. O clique é quem desfaz o Desligar.
 *
 * Módulo de `lib/push`, sem React. Fala com o servidor pelo `clienteDoPush(basePath)` (a escolha entre o axios interno e o do portal
 * mora lá, num lugar só).
 */
import { clienteDoPush } from "@/hooks/clienteDoPush";

import { appDoPush, lerDonoDoPush, marcarDonoDoPush } from "./donoDoPush";
import {
  avisarQueOPushMudou,
  lerIntencaoDoPush,
  marcaDoEndpoint,
  registrarConfirmacaoDoPush,
  registrarIntencaoDoPush,
} from "./memoriaDoPush";

/** Quem pediu a inscrição: vai no corpo do `POST /subscribe` (o servidor registra a origem no log). */
export type OrigemDaInscricao = "clique" | "automatico" | "renovada";

/** Quanto esperar o `navigator.serviceWorker.ready` antes de desistir (um SW que nunca ativa não pode prender o religar para sempre). */
export const ESPERA_DO_SERVICE_WORKER_MS = 10_000;

/** Mesmo service worker que o resto do app registra (`lib/sw-registration.ts`). */
const SERVICE_WORKER_URL = "/service-worker.js";

/** O servidor não devolveu a chave VAPID pública: sem ela não há como inscrever. */
export class SemChaveVapidError extends Error {
  constructor() {
    super("O servidor não devolveu a chave VAPID do push");
    this.name = "SemChaveVapidError";
  }
}

/** A intenção virou `desligado` no meio de um religar: a pessoa apertou Desligar e o religar sozinho não pode desfazer isso. */
export class DesligadoDePropositoError extends Error {
  constructor() {
    super("O push foi desligado de propósito neste aparelho; o religar sozinho não o desfaz");
    this.name = "DesligadoDePropositoError";
  }
}

/**
 * O `unsubscribe()` da inscrição velha devolveu `false`: o navegador NÃO a desfez. Seguir para o `subscribe()` devolveria a mesma
 * inscrição morta, e o POST recriaria a linha morta no servidor, a cada intervalo, com um 410 por volta. O `sincronizarPush` trata como
 * qualquer falha do religar (conta; a 3ª seguida vira o aviso com o botão).
 */
export class NaoDesinscreveuError extends Error {
  constructor() {
    super("O navegador não desfez a inscrição de push antiga");
    this.name = "NaoDesinscreveuError";
  }
}

export interface OpcoesDeEnvio {
  /**
   * Perguntado DEPOIS do POST. `false` = a sessão acabou no meio (um "Sair"): não grava dono, intenção nem confirmação, só avisa a tela.
   * Sem a opção, vale como `true`.
   */
  aindaVale?: () => boolean;
}

/**
 * Converte chave VAPID base64url (string) para Uint8Array, formato exigido por `PushManager.subscribe({ applicationServerKey })`.
 *
 * VAPID keys vêm em base64url ("-_" em vez de "+/", sem padding "="). `atob()` exige base64 padrão com padding: esta é a fonte mais
 * comum de bug em clientes Web Push.
 */
function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

/** Espera o service worker ativar, com prazo: estourando, lança um `Error` legível (vira o texto do aviso/erro na tela). */
async function esperarOServiceWorker(): Promise<void> {
  let prazo: ReturnType<typeof setTimeout> | undefined;
  const estouro = new Promise<never>((_, rejeitar) => {
    prazo = setTimeout(
      () =>
        rejeitar(
          new Error(
            `O service worker não ficou pronto em ${ESPERA_DO_SERVICE_WORKER_MS / 1000} s; recarregue a página e tente de novo`
          )
        ),
      ESPERA_DO_SERVICE_WORKER_MS
    );
  });
  try {
    await Promise.race([navigator.serviceWorker.ready, estouro]);
  } finally {
    // Se o `ready` ganhou, o prazo não pode ficar pendurado (nem rejeitar sozinho depois, sem ninguém para ouvir).
    clearTimeout(prazo);
  }
}

/**
 * Manda a inscrição ao servidor e, só se o POST deu certo, grava o que o aparelho precisa lembrar (ver o topo do arquivo).
 * O `sincronizarPush` a chama direto para ADOTAR um endpoint vivo que o servidor não tem, sem trocar de endpoint.
 */
export async function enviarInscricaoAoServidor(
  basePath: string,
  sub: PushSubscription,
  origem: OrigemDaInscricao,
  opcoes: OpcoesDeEnvio = {}
): Promise<void> {
  await clienteDoPush(basePath).post(`${basePath}/subscribe`, {
    ...sub.toJSON(),
    user_agent: navigator.userAgent,
    origem,
  });
  // O POST deu certo; se um "Sair" aconteceu enquanto ele voava, nada fica gravado (marca órfã, sem sessão por trás). A tela relê
  // o estado do mesmo jeito.
  if (opcoes.aindaVale?.() === false) {
    avisarQueOPushMudou();
    return;
  }
  const app = appDoPush(basePath);
  // O servidor ficou com este app: o clique toma o aparelho; as outras origens só adotam quando ninguém é dono.
  if (origem === "clique" || lerDonoDoPush() === null) marcarDonoDoPush(app);
  registrarIntencaoDoPush("ligado");
  registrarConfirmacaoDoPush(app, marcaDoEndpoint(sub.endpoint));
  avisarQueOPushMudou();
}

/**
 * Inscreve este aparelho no push. Ordem: service worker registrado e pronto → chave VAPID → (fora do clique) confere que o Desligar
 * não foi gravado nesse meio-tempo → `substituir?.unsubscribe()` → `pushManager.subscribe()` → `enviarInscricaoAoServidor`.
 *
 * `substituir` é a inscrição velha, que o servidor largou: o `unsubscribe()` dela vem ANTES do `subscribe()`, porque `subscribe()` com
 * a velha ainda de pé devolve a mesma inscrição morta (Decisão 2).
 *
 * Lança `SemChaveVapidError` (servidor sem chave), `DesligadoDePropositoError` (ver o topo), `NaoDesinscreveuError` (o `unsubscribe()`
 * da velha devolveu `false`), um `Error` legível se o service worker não ficar pronto em `ESPERA_DO_SERVICE_WORKER_MS`, e o erro de
 * rede/navegador que vier (o chamador decide o que mostrar).
 */
export async function inscreverEsteAparelho(args: {
  basePath: string;
  origem: OrigemDaInscricao;
  substituir?: PushSubscription | null;
  aindaVale?: () => boolean;
}): Promise<PushSubscription> {
  const { basePath, origem, substituir, aindaVale } = args;

  const reg =
    (await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL)) ||
    (await navigator.serviceWorker.register(SERVICE_WORKER_URL));
  // Garante que está ativo antes de tentar subscribe.
  await esperarOServiceWorker();

  const { data } = await clienteDoPush(basePath).get<{ public_key?: string }>(
    `${basePath}/vapid-key`
  );
  if (!data?.public_key) throw new SemChaveVapidError();

  // Relê a intenção AGORA, depois da última espera de rede e antes de mexer no navegador: um Desligar gravado durante o `vapid-key`
  // (noutra aba, ou nesta) não pode ser desfeito por um religar que já tinha passado da checagem do `sincronizarPush`. O clique não
  // passa por aqui: ele é justamente quem desfaz o Desligar.
  if (origem !== "clique" && lerIntencaoDoPush() === "desligado") {
    throw new DesligadoDePropositoError();
  }

  if (substituir) {
    // `false` = não desfez (o `true` e a rejeição são os outros dois desfechos): sem `subscribe` nem POST, que só trariam a morta de volta.
    if ((await substituir.unsubscribe()) === false) throw new NaoDesinscreveuError();
  }

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast: TS 5.7+ tipa Uint8Array como Uint8Array<ArrayBufferLike>, mas PushSubscribeOptions.applicationServerKey espera
    // BufferSource (anti-SharedArrayBuffer). O buffer aqui é sempre ArrayBuffer regular.
    applicationServerKey: base64UrlToUint8Array(data.public_key) as BufferSource,
  });

  await enviarInscricaoAoServidor(basePath, sub, origem, { aindaVale });
  return sub;
}
